import { z } from 'zod';
import type { operations } from '@molchanovs/api-contract';
import { api, isApiError, bubbleMutationOperationStatusSchema } from '@shared/api';
import { clearQueryResponseCache } from '@shared/lib/query-helpers';
import { helpdeskThreadSchema } from './thread';
const receiptSchema = z.object({
  operationId: z.uuid(),
  status: z.string(),
  replayed: z.boolean(),
  requestFingerprint: z.string(),
});
// Operation discovery also reports supersession; the shared receipt schema omits it.
const statusSchema = bubbleMutationOperationStatusSchema.or(z.literal('superseded'));
const operationSchema: z.ZodType<
  operations['getBubbleMutationOperationStatus']['responses'][200]['content']['application/json']
> = z.object({
  operationId: z.uuid(),
  operationStatus: statusSchema,
  deliveryState: z.enum([
    'pending',
    'processing',
    'retrying',
    'applied',
    'superseded',
    'manual_reconciliation',
    'failed',
  ]),
  businessState: z.enum(['accepted', 'succeeded', 'failed']),
  steps: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
    applied: z.number().int().nonnegative(),
    superseded: z.number().int().nonnegative(),
    manualReconciliation: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  eligibility: z.object({
    nextAttemptAt: z.iso.datetime({ offset: true }).nullable(),
    blockedByEarlierOperation: z.boolean(),
    earlierOwnedOperation: z
      .object({
        operationId: z.uuid(),
        operationStatus: statusSchema,
        familyKey: z.string().trim().min(1).max(120),
        operationKey: z.string().trim().min(1).max(120),
      })
      .nullable(),
    blockedByDependency: z.boolean(),
    runtimePaused: z.boolean(),
  }),
  resultState: z.enum(['pending', 'available', 'expired', 'unavailable']),
  resultExpiresAt: z.iso.datetime({ offset: true }).nullable(),
  compensatesOperationId: z.uuid().nullable(),
  deliveryAppliedAt: z.iso.datetime({ offset: true }).nullable(),
  conflictAt: z.iso.datetime({ offset: true }).nullable(),
  acceptedAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  completedAt: z.iso.datetime({ offset: true }).nullable(),
  failureCode: z
    .string()
    .regex(/^[A-Z0-9_]{1,120}$/u)
    .nullable(),
});
export async function admitHelpdeskDeletion(id: string, key: string, signal: AbortSignal) {
  return api.delete(`/api/v1/tasks/${encodeURIComponent(z.uuid().parse(id))}`, {
    headers: { 'Idempotency-Key': key },
    signal,
    schema: receiptSchema,
  });
}
export async function inspectHelpdeskDeletion(
  operationId: string,
  masterId: string,
  targetId: string,
  signal: AbortSignal
): Promise<'pending' | 'complete' | 'failed'> {
  const operation = await api.get(`/api/v1/operations/${encodeURIComponent(operationId)}`, {
    signal,
    schema: operationSchema,
  });
  if (
    ['terminal_failure', 'conflict', 'manual_reconciliation', 'superseded'].includes(
      operation.operationStatus
    )
  )
    return 'failed';
  if (operation.operationStatus !== 'applied') return 'pending';
  if (masterId === targetId) {
    try {
      await api.get(`/api/v1/tasks/${encodeURIComponent(targetId)}`, { signal });
      return 'pending';
    } catch (error) {
      if (isApiError(error) && error.status === 404) {
        clearQueryResponseCache();
        return 'complete';
      }
      throw error;
    }
  }
  let page = 1;
  let totalPages = 1;
  do {
    const thread = await api.get(`/api/v1/tasks/${encodeURIComponent(masterId)}/replies`, {
      signal,
      params: { page, pageSize: 100 },
      schema: helpdeskThreadSchema,
    });
    if (thread.items.some((item) => item.id === targetId)) return 'pending';
    totalPages = thread.meta.totalPages;
    page += 1;
  } while (page <= totalPages);
  clearQueryResponseCache();
  return 'complete';
}
